import { getCsrfToken } from "@/app/_utils/csrf";

export interface PromoCode {
  id: string;
  code: string;
  discount_percent: number;
  active: boolean;
  /** Show ids this code applies to. An empty list means it applies to every show. */
  shows: string[];
  created_at: string;
  updated_at: string;
}

export interface PromoUpcomingShow {
  id: string;
  title: string;
  start: string;
  variant_name?: string | null;
  img_url?: string | null;
  is_header: boolean;
  children?: PromoUpcomingShow[];
}

/** A single selectable show, with header/child rows already flattened. */
export interface PromoShowOption {
  id: string;
  label: string;
  start: string;
  img_url?: string | null;
}

export interface PromoKpis {
  uses: number;
  tickets: number;
  discount_amount: number;
  gross_revenue: number;
  shows_used: number;
  unique_guests: number;
}

export interface PromoShowUsageRow {
  id: string;
  title: string | null;
  variant_name?: string | null;
  start?: string | null;
  img_url?: string | null;
  uses: number;
  tickets: number;
  discount_amount: number;
  revenue: number;
}

export interface PromoDetail {
  promo: PromoCode;
  kpis: PromoKpis;
  shows: PromoShowUsageRow[];
}

export interface PromoPayload {
  code: string;
  discount_percent: number;
  active: boolean;
  shows: string[];
}

class PromoService {
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}promos/`;

  private get csrf() {
    return getCsrfToken();
  }

  /**
   * Throws on non-2xx so callers can surface the real server message (e.g. a duplicate
   * code) instead of treating a validation error as a successful save.
   */
  private async handle(res: Response) {
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(errorMessage(body) ?? `Request failed (${res.status})`);
    return body;
  }

  getPromoCodesAsync = async (): Promise<PromoCode[]> => {
    const res = await fetch(this.baseUrl, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return this.handle(res);
  };

  getPromoCodeAsync = async (id: string): Promise<PromoCode> => {
    const res = await fetch(`${this.baseUrl}${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return this.handle(res);
  };

  /** Usage analytics for one code: KPIs plus a per-show breakdown. */
  getPromoDetailAsync = async (id: string): Promise<PromoDetail> => {
    const res = await fetch(`${this.baseUrl}${id}/detail/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return this.handle(res);
  };

  createPromoCodeAsync = async (data: PromoPayload): Promise<PromoCode> => {
    const res = await fetch(this.baseUrl, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf },
      credentials: "include",
    });
    return this.handle(res);
  };

  updatePromoCodeAsync = async (id: string, data: Partial<PromoPayload>): Promise<PromoCode> => {
    const res = await fetch(`${this.baseUrl}${id}/`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf },
      credentials: "include",
    });
    return this.handle(res);
  };

  deletePromoCodeAsync = async (id: string) => {
    const res = await fetch(`${this.baseUrl}${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": this.csrf },
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return res;
  };

  getUpcomingShowsAsync = async (): Promise<PromoUpcomingShow[]> => {
    const res = await fetch(`${this.baseUrl}upcoming-shows/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return this.handle(res);
  };

  validatePromoCodeAsync = async (code: string) => {
    const res = await fetch(`${this.baseUrl}validate/`, {
      method: "POST",
      body: JSON.stringify({ code }),
      headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf },
      credentials: "include",
    });
    return res.json();
  };
}

/** Pulls a readable message out of a DRF error body, e.g. `{ code: ["...already exists."] }`. */
function errorMessage(body: any): string | null {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (typeof body.detail === "string") return body.detail;
  const first = Object.values(body)[0];
  if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  if (typeof first === "string") return first;
  return null;
}

/** Flattens the upcoming-shows response (headers + their variant children) into pickable rows. */
export function flattenShowOptions(shows: PromoUpcomingShow[]): PromoShowOption[] {
  return shows.flatMap((show) =>
    show.is_header
      ? (show.children ?? []).map((child) => ({
          id: child.id,
          label: child.variant_name ? `${show.title} — ${child.variant_name}` : show.title,
          start: child.start,
          img_url: child.img_url,
        }))
      : [{ id: show.id, label: show.title, start: show.start, img_url: show.img_url }]
  );
}

export default new PromoService();
